// src/components/AdminPanel.js
import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { doc, setDoc, collection, writeBatch, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Papa from 'papaparse';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

export default function AdminPanel({ onClose }) {
  const [file, setFile]         = useState(null);
  const [status, setStatus]     = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [currentMeta, setCurrentMeta]       = useState(null);
  const [grapeStatus, setGrapeStatus]       = useState(null);
  const [grapeSeeding, setGrapeSeeding]     = useState(false);

  useEffect(() => {
    // Load grape index metadata
    Promise.all([
      getDoc(doc(db, 'grape_index', 'washington')),
      getDoc(doc(db, 'grape_index', 'oregon')),
    ]).then(([waSnap, orSnap]) => {
      if (waSnap.exists() || orSnap.exists()) {
        setGrapeStatus({
          wa: waSnap.exists() ? waSnap.data().count : 0,
          or: orSnap.exists() ? orSnap.data().count : 0,
          seededAt: waSnap.exists() ? waSnap.data().seededAt : null,
        });
      }
    }).catch(() => {});

    getDoc(doc(db, 'hhp_metadata', 'current')).then(snap => {
      if (snap.exists()) setCurrentMeta(snap.data());
    }).catch(() => {});
  }, []);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setStatus(null);
    setPreview(null);

    try {
      const text = await f.text();
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows = results.data;
          const prohibited = rows.filter(r => r['List Status'] === 'PROHIBITED');
          const watchlist  = rows.filter(r => r['List Status'] === 'WATCH LIST');
          setPreview({
            totalRows: rows.length,
            prohibitedCount: prohibited.length,
            watchlistCount: watchlist.length,
            columns: results.meta.fields,
            sampleProhibited: prohibited[0]?.['Active Ingredient'],
            sampleWatchlist:  watchlist[0]?.['Active Ingredient'],
          });
        },
        error: (err) => {
          setStatus({ type: 'error', message: 'Could not parse CSV: ' + err.message });
        }
      });
    } catch (err) {
      setStatus({ type: 'error', message: 'Could not read file: ' + err.message });
    }
  };

  const handleSeedGrape = async () => {
    setGrapeSeeding(true);
    setStatus({ type: 'info', message: 'Seeding grape label index from PICOL — this may take 30-60 seconds...' });
    try {
      const functions = getFunctions(app);
      const seedGrapeIndex = httpsCallable(functions, 'seedGrapeIndex', { timeout: 600000 });
      const result = await seedGrapeIndex();
      setGrapeStatus({
        wa: result.data.washington,
        or: result.data.oregon,
        seededAt: { toDate: () => new Date() },
      });
      setStatus({
        type: 'success',
        message: `✓ Grape index seeded! ${result.data.washington} WA products + ${result.data.oregon} OR products labeled for grapes.`,
      });
    } catch (err) {
      setStatus({ type: 'error', message: 'Grape index seed failed: ' + err.message });
    } finally {
      setGrapeSeeding(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !preview) return;
    setUploading(true);
    setStatus({ type: 'info', message: 'Reading and parsing file...' });

    try {
      const text = await file.text();
      let rows = [];

      await new Promise((resolve, reject) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => { rows = results.data; resolve(); },
          error: reject,
        });
      });

      const prohibited = rows.filter(r => r['List Status'] === 'PROHIBITED');
      const watchlist  = rows.filter(r => r['List Status'] === 'WATCH LIST');

      setStatus({ type: 'info', message: 'Uploading file to storage...' });

      // Upload raw file to Storage for archiving
      const storageRef = ref(storage, `hhp-uploads/${Date.now()}-${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      setStatus({ type: 'info', message: `Saving ${prohibited.length} prohibited materials...` });

      // Helper
      const yn = (val) => val === 'Yes';

      // Build prohibited docs
      const prohibitedDocs = prohibited.map((row, i) => {
        const name = row['Active Ingredient'].trim();
        return {
          name,
          nameUpper: name.toUpperCase(),
          nameBase:  name.toUpperCase().split(',')[0].trim(),
          cas:       row['CAS Number']?.trim() || '',
          listStatus: 'PROHIBITED',
          riskCategory: 'Prohibited — Internationally Banned',
          convention: row['Convention / Basis']?.trim() || '',
          pic:       yn(row['PIC Convention']),
          pop:       yn(row['POP Convention']),
          montreal:  yn(row['Montreal Protocol']),
          sources: {
            panHHP2024:     yn(row['On PAN HHP 2024 List']),
            swProhibited2026: yn(row['On SustainableWA Prohibited 2026']),
            swWatchList2026:  yn(row['On SustainableWA Watch List 2026']),
          },
        };
      });

      // Build watch list docs
      const watchlistDocs = watchlist.map((row) => {
        const name = row['Active Ingredient'].trim();
        return {
          name,
          nameUpper: name.toUpperCase(),
          nameBase:  name.toUpperCase().split(',')[0].trim(),
          cas:       row['CAS Number']?.trim() || '',
          listStatus: 'WATCH LIST',
          riskCategory: row['Risk Category']?.trim() || '',
          convention: '',
          criteria: {
            acuteToxicity: {
              whoIa: yn(row['WHO Class Ia (Extremely Hazardous)']),
              whoIb: yn(row['WHO Class Ib (Highly Hazardous)']),
              h330:  yn(row['Fatal if Inhaled (H330)']),
            },
            longTermHealth: {
              epaCarc:      yn(row['EPA Carcinogen']),
              iarcCarc:     yn(row['IARC Carcinogen']),
              ghsCarc:      yn(row['GHS Carcinogen (1A/1B)']),
              iarcProbCarc: yn(row['IARC Probable Carcinogen']),
              epaProbCarc:  yn(row['EPA Probable Carcinogen']),
              ghsMuta:      yn(row['GHS Mutagen (1A/1B)']),
              ghsRepro:     yn(row['GHS Reproductive Toxicant (1A/1B)']),
              euEdc:        yn(row['EU Endocrine Disruptor']),
              ghsC2r2:      yn(row['GHS Suspected Repro Toxicant']),
            },
            environmentalToxicity: {
              veryBioAcc:      yn(row['Very Bioaccumulative']),
              veryPers:        yn(row['Very Persistent']),
              veryToxicAq:     yn(row['Very Toxic to Aquatic Organisms']),
              highlyToxicBees: yn(row['Highly Toxic to Bees']),
            },
            conventions: {
              montreal: yn(row['Montreal Protocol']),
              pic:      yn(row['PIC Convention']),
              pop:      yn(row['POP Convention']),
            },
          },
          sources: {
            panHHP2024:      yn(row['On PAN HHP 2024 List']),
            swProhibited2026: yn(row['On SustainableWA Prohibited 2026']),
            swWatchList2026:  yn(row['On SustainableWA Watch List 2026']),
          },
        };
      });

      // Clear and reseed hhp_prohibited
      const BATCH = 400;
      for (let i = 0; i < prohibitedDocs.length; i += BATCH) {
        const batch = writeBatch(db);
        prohibitedDocs.slice(i, i + BATCH).forEach((d, j) => {
          batch.set(doc(collection(db, 'hhp_prohibited'), String(i + j)), d);
        });
        await batch.commit();
      }

      setStatus({ type: 'info', message: `Saving ${watchlistDocs.length} watch list chemicals...` });

      // Clear and reseed hhp_watchlist
      for (let i = 0; i < watchlistDocs.length; i += BATCH) {
        const batch = writeBatch(db);
        watchlistDocs.slice(i, i + BATCH).forEach((d, j) => {
          batch.set(doc(collection(db, 'hhp_watchlist'), String(i + j)), d);
        });
        await batch.commit();
      }

      // Save metadata
      const meta = {
        uploadedAt:    new Date().toISOString(),
        fileName:      file.name,
        downloadUrl,
        prohibitedCount: prohibitedDocs.length,
        watchListCount:  watchlistDocs.length,
        version: `Uploaded ${new Date().toLocaleDateString()} — ${file.name}`,
      };
      await setDoc(doc(db, 'hhp_metadata', 'current'), meta);
      setCurrentMeta(meta);

      setStatus({
        type: 'success',
        message: `✓ Upload complete! ${prohibitedDocs.length} prohibited + ${watchlistDocs.length} watch list chemicals saved. The app will use this data immediately.`,
      });
    } catch (err) {
      setStatus({ type: 'error', message: 'Upload failed: ' + err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal admin-modal">
        <div className="modal-header">
          <h2>Admin Panel</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">

          {/* Current database status */}
          {currentMeta && (
            <div className="admin-section admin-current">
              <h3>Current Database</h3>
              <div className="current-meta">
                <span>📋 {currentMeta.prohibitedCount} prohibited · {currentMeta.watchListCount} watch list</span>
                <span>📅 {currentMeta.version}</span>
              </div>
            </div>
          )}

          {/* Upload section */}
          <div className="admin-section">
            <h3>Update HHP List</h3>
            <p className="admin-note">
              Upload the <strong>SustainableWA_HHP_Master_List.csv</strong> file to update the database.
              This file is generated from the PAN HHP List and SustainableWA Winegrape Standard.
              The app will use the new data immediately after upload — no redeployment needed.
            </p>

            <div className="upload-zone">
              <input type="file" accept=".csv" onChange={handleFileChange}
                id="hhp-upload" className="file-input" />
              <label htmlFor="hhp-upload" className="upload-label">
                <span className="upload-icon">📂</span>
                <span>{file ? file.name : 'Click to select SustainableWA_HHP_Master_List.csv'}</span>
              </label>
            </div>

            {preview && (
              <div className="upload-preview">
                <p>✓ File parsed successfully</p>
                <p>Prohibited materials: <strong>{preview.prohibitedCount}</strong> (e.g. {preview.sampleProhibited})</p>
                <p>Watch list chemicals: <strong>{preview.watchlistCount}</strong> (e.g. {preview.sampleWatchlist})</p>
                <p>Total: <strong>{preview.totalRows}</strong> chemicals</p>
              </div>
            )}

            {status && (
              <div className={`upload-status status-${status.type}`}>
                {status.message}
              </div>
            )}

            <button className="upload-btn" onClick={handleUpload}
              disabled={!file || uploading || !preview}>
              {uploading ? 'Uploading...' : 'Upload to Database'}
            </button>
          </div>

          {/* Grape Index Section */}
          <div className="admin-section">
            <h3>Grape Label Index</h3>
            <p className="admin-note">
              Builds a list of all EPA numbers registered for use on grapes in WA and OR,
              by querying PICOL directly. Run this after initial setup and whenever PICOL
              data has significantly changed (annually or as needed).
            </p>
            {grapeStatus && (
              <div className="upload-preview">
                <p>✓ Index loaded: <strong>{grapeStatus.wa} WA</strong> + <strong>{grapeStatus.or} OR</strong> grape-labeled products</p>
              </div>
            )}
            {!grapeStatus && (
              <div className="upload-status status-info">
                Grape index not yet seeded. Click below to build it.
              </div>
            )}
            <button className="upload-btn" onClick={handleSeedGrape} disabled={grapeSeeding}>
              {grapeSeeding ? 'Seeding... (30-60 seconds)' : 'Seed / Refresh Grape Index'}
            </button>
          </div>

          {/* Instructions */}
          <div className="admin-section admin-info">
            <h3>Update Process (every 2 years)</h3>
            <ol className="admin-steps">
              <li>Get updated files from PAN (<code>PAN_HHP_List.xlsx</code>) and SustainableWA (<code>Prohibited_List.xlsx</code>)</li>
              <li>Run the Python generation script to create a new <code>SustainableWA_HHP_Master_List.csv</code></li>
              <li>Upload that CSV here — the app updates instantly, no code changes needed</li>
            </ol>
          </div>

        </div>
      </div>
    </div>
  );
}
